import { z } from 'zod';

import { passwordSchema } from './auth.js';
import { emailSchema, dateStringSchema } from './common.js';
import { paginationSchema, sortSchema } from './pagination.js';

// ---------------------------------------------------------------------------
// Taxonomy
// ---------------------------------------------------------------------------

export const createTaxonomySchema = z
  .object({
    name: z.string().min(1).max(200).trim(),
    description: z.string().max(1000).trim().optional(),
    parentId: z.string().uuid().nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
    isTenantWide: z.boolean().optional(),
    organizationIds: z.array(z.string().uuid()).optional(),
  })
  .strict();

export const updateTaxonomySchema = z
  .object({
    name: z.string().min(1).max(200).trim().optional(),
    description: z.string().max(1000).trim().optional(),
    parentId: z.string().uuid().nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .strict();

// ---------------------------------------------------------------------------
// Dictionary
// ---------------------------------------------------------------------------

export const createDictionarySchema = z
  .object({
    name: z.string().min(1).max(200).trim(),
    description: z.string().max(1000).trim().optional(),
    taxonomyId: z.string().uuid().optional(),
  })
  .strict();

export const updateDictionarySchema = z
  .object({
    name: z.string().min(1).max(200).trim().optional(),
    description: z.string().max(1000).trim().optional(),
    taxonomyId: z.string().uuid().nullable().optional(),
    probeDurationSeconds: z.number().int().min(60).max(3600).optional(),
  })
  .strict();

export const publishDictionarySchema = z.object({}).strict();

export const createNewVersionSchema = z.object({}).strict();

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

const definitionMaxWords = (val: string) => val.split(/\s+/).filter(Boolean).length <= 15;

/** Every vocabulary entry must have a definition (vocabulary-matching probes). */
export const vocabularyDefinitionSchema = z
  .string()
  .trim()
  .min(1, 'Definition is required')
  .max(2000)
  .refine(definitionMaxWords, { message: 'Definition must not exceed 15 words' });

/** Strip legacy exampleSentence from requests; DB column may remain unused. */
function stripVocabularyExampleField<T extends { exampleSentence?: unknown }>(
  data: T,
): Omit<T, 'exampleSentence'> {
  const { exampleSentence: _ignored, ...rest } = data;
  return rest;
}

export const createVocabularySchema = z
  .object({
    dictionaryId: z.string().uuid(),
    word: z.string().min(1).max(200).trim(),
    definition: vocabularyDefinitionSchema,
    exampleSentence: z.string().max(2000).trim().optional(),
    imageUrl: z.string().url().optional(),
    audioUrl: z.string().url().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .strict()
  .transform(stripVocabularyExampleField);

export const updateVocabularySchema = z
  .object({
    dictionaryId: z.string().uuid(),
    word: z.string().min(1).max(200).trim().optional(),
    definition: vocabularyDefinitionSchema.optional(),
    exampleSentence: z.string().max(2000).trim().nullable().optional(),
    imageUrl: z.string().url().nullable().optional(),
    audioUrl: z.string().url().nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .strict()
  .transform(stripVocabularyExampleField);

export const bulkCreateVocabularySchema = z.array(createVocabularySchema).min(1).max(100);

// ---------------------------------------------------------------------------
// Classroom
// ---------------------------------------------------------------------------

export const createClassroomSchema = z
  .object({
    name: z.string().min(1).max(200).trim(),
    sectionNumber: z.string().max(50).trim().optional(),
    gradeLevel: z.string().max(50).trim().optional(),
    subject: z.string().max(100).trim().optional(),
    academicYearId: z.string().uuid().optional(),
    termId: z.string().uuid().optional(),
    orgUnitId: z.string().uuid().optional(),
    dictionaryId: z.string().uuid().optional(),
    startDate: dateStringSchema,
    endDate: dateStringSchema,
  })
  .strict();

export const updateClassroomSchema = z
  .object({
    name: z.string().min(1).max(200).trim().optional(),
    sectionNumber: z.string().max(50).trim().optional(),
    gradeLevel: z.string().max(50).trim().optional(),
    subject: z.string().max(100).trim().optional(),
    academicYearId: z.string().uuid().nullable().optional(),
    termId: z.string().uuid().nullable().optional(),
    orgUnitId: z.string().uuid().nullable().optional(),
    dictionaryId: z.string().uuid().nullable().optional(),
    startDate: dateStringSchema.optional(),
    endDate: dateStringSchema.optional(),
  })
  .strict();

export const archiveClassroomSchema = z.object({}).strict();

// ---------------------------------------------------------------------------
// Probe
// ---------------------------------------------------------------------------

export const enableProbeSchema = z
  .object({
    availabilityMinutes: z.number().int().min(10),
    // B24: optional future open time (ISO-8601 UTC). When present the probe is
    // scheduled — it becomes launchable only at/after this instant. When
    // omitted the probe opens immediately (legacy behavior).
    scheduledOpenAt: z.string().datetime().optional(),
  })
  .strict();

// B24: enable many probes in one call (e.g. release a whole week's set, or
// bulk-schedule). Same window semantics as enableProbeSchema, applied to each
// probe id.
export const bulkEnableProbesSchema = z
  .object({
    probeIds: z.array(z.string().uuid()).min(1).max(100),
    availabilityMinutes: z.number().int().min(10),
    scheduledOpenAt: z.string().datetime().optional(),
  })
  .strict();

export const reEnableProbeSchema = z
  .object({
    learnerId: z.string().uuid().optional(),
    learnerIds: z.array(z.string().uuid()).min(1).max(200).optional(),
    availabilityMinutes: z.number().int().min(10).optional(),
  })
  .strict();

// ---------------------------------------------------------------------------
// Probe Session
// ---------------------------------------------------------------------------

export const startProbeSessionSchema = z
  .object({
    probeId: z.string().uuid(),
    classroomId: z.string().uuid(),
  })
  .strict();

export const answerProbeItemSchema = z
  .object({
    probeItemId: z.string().uuid(),
    selectedAnswerId: z.string().uuid(),
  })
  .strict();

export const submitProbeSchema = z
  .object({
    matches: z.array(
      z
        .object({
          probeItemId: z.string().uuid(),
          selectedAnswerId: z.string().uuid(),
        })
        .strict(),
    ),
    /** How the learner felt before starting (collected after instructions). */
    sentimentBefore: z.number().int().min(1).max(3).optional(),
    /** How the learner felt after finishing (post-probe). Stored as sentiment_rating. */
    sentimentRating: z.number().int().min(1).max(3).optional(),
  })
  .strict();

// C19: set/update post-probe sentiment after the fact when not captured inline.
export const setSentimentSchema = z
  .object({
    sentimentRating: z.number().int().min(1).max(3),
  })
  .strict();

// ---------------------------------------------------------------------------
// Enrollment
// ---------------------------------------------------------------------------

export const generateEnrollmentCodeSchema = z
  .object({
    classroomId: z.string().uuid(),
    expiresAt: z.string().datetime().optional(),
    maxUses: z.number().int().min(1).optional(),
  })
  .strict();

export const validateEnrollmentCodeSchema = z
  .object({
    code: z.string().min(4).max(10).trim(),
    turnstileToken: z.string().optional(),
  })
  .strict();

export const studentSelfRegisterSchema = z
  .object({
    code: z.string().min(4).max(10).trim(),
    firstName: z.string().min(1).max(100).trim(),
    lastName: z.string().min(1).max(100).trim(),
    email: emailSchema,
    // C7: password is only required for new accounts on password tenants (the
    // register endpoint enforces that). Magic-link tenants create the account
    // without one. An empty string is accepted (and treated as absent by the
    // handler) so a magic-link submit that still includes an empty password
    // field validates cleanly instead of tripping the min-length rule.
    // C23: when a password IS supplied it must meet the platform password policy
    // (passwordSchema) so the server matches what the client already enforces —
    // otherwise a direct API call could create a learner with a weak 8-char
    // password the UI would never allow.
    password: passwordSchema.optional().or(z.literal('')),
    // The SPA sends the Turnstile token in the body (the kiosk RegisterPage has
    // no way to set request headers). Optional so the e2e bypass header path and
    // magic-link tenants still validate. (CONTRACT: C1)
    turnstileToken: z.string().optional(),
    // C14: COPPA/age attestation. Optional at the schema level so callers that
    // don't surface the checkbox still validate; the register endpoint enforces
    // it when the tenant has requires_parental_consent set, and records consent
    // whenever it is provided.
    coppaConsent: z.boolean().optional(),
  })
  .strict();

export const requestOtpSchema = z
  .object({
    email: emailSchema,
  })
  .strict();

export const verifyOtpSchema = z
  .object({
    email: emailSchema,
    code: z.string().length(6),
  })
  .strict();

// ---------------------------------------------------------------------------
// Goal Configuration (PlatformAdmin per-course settings)
// ---------------------------------------------------------------------------

export const goalConfigSchema = z
  .object({
    goalGrowthRate: z.number().min(0.01).max(10).optional(),
    goalColorVariance: z.number().min(0.01).max(10).optional(),
    /** Instructional pass floor percent (default 75). Maps to points via itemCount. */
    goalPassFloorPercent: z.number().min(50).max(100).optional(),
  })
  .strict();

// ---------------------------------------------------------------------------
// Invitation Module
// ---------------------------------------------------------------------------

export const invitationModuleSchema = z.enum(['pilot', 'progress']).optional();

// ---------------------------------------------------------------------------
// Query / Pagination
// ---------------------------------------------------------------------------

const classroomStatusEnum = z.enum(['active', 'archived']);

export const classroomQuerySchema = paginationSchema
  .merge(sortSchema)
  .merge(
    z.object({
      search: z.string().max(200).trim().optional(),
      status: classroomStatusEnum.optional(),
      academicYearId: z.string().uuid().optional(),
      termId: z.string().uuid().optional(),
    }),
  )
  .strict();

export const dictionaryQuerySchema = paginationSchema
  .merge(sortSchema)
  .merge(
    z.object({
      search: z.string().max(200).trim().optional(),
      isPublished: z.coerce.boolean().optional(),
      taxonomyId: z.string().uuid().optional(),
    }),
  )
  .strict();

const probeStatusEnum = z.enum(['draft', 'published', 'archived']);

export const probeQuerySchema = paginationSchema
  .merge(sortSchema)
  .merge(
    z.object({
      search: z.string().max(200).trim().optional(),
      status: probeStatusEnum.optional(),
      dictionaryId: z.string().uuid().optional(),
    }),
  )
  .strict();

const probeSessionStatusEnum = z.enum(['in_progress', 'completed', 'abandoned']);

export const probeSessionQuerySchema = paginationSchema
  .merge(sortSchema)
  .merge(
    z.object({
      classroomId: z.string().uuid().optional(),
      learnerId: z.string().uuid().optional(),
      probeId: z.string().uuid().optional(),
      status: probeSessionStatusEnum.optional(),
    }),
  )
  .strict();
